/** A paused, guided look around the live tutorial screen. */
type Stop = { target: string; title: string; body: string; prepare?: () => void };

export function startTutorialTour(root: HTMLElement, onSelect: (callsign: string | null) => void, onDone: () => void): () => void {
  const stops: Stop[] = [
    { target: '.g-runways', title: 'Runway and traffic', body: 'Both arrivals and departures use runway 12. This header shows the active runways; the nearby status tracks aircraft and your watch.' },
    { target: '.g-views', title: 'Choose a view', body: 'Radar, Split and Tower change what you see. Split is open now so you can follow the radar and the tower at once. You can switch views during a watch.' },
    { target: '.g-scope', title: 'Radar scope', body: 'Aircraft targets move across the scope. Click a target to select it and open its command card. The arrival is approaching runway 12.' },
    { target: '.g-tower', title: 'Tower view', body: 'The 3D view shows the runway and nearby traffic. Click an aircraft here to select it, or use the radar and strips.' },
    { target: '.g-strips', title: 'Flight strips', body: 'Each strip summarizes one aircraft. Click ELY312 or ISR204 here to select it. The blue departure is holding short of runway 12.' },
    { target: '.g-comms', title: 'Radio log', body: 'Pilot check-ins, your clearances and readbacks appear here in order. Look here to confirm a call was heard.' },
    { target: '.g-command', title: 'Type a command', body: 'You can type a callsign and command here during a watch. This tutorial guides you through the aircraft card buttons first.' },
    { target: '.g-voice', title: 'Push to talk', body: 'Push to talk is on by default. Hold its button, Space or Alt+R to speak when the microphone service is available; release to transmit.' },
    { target: '.acard', title: 'Aircraft command card', body: 'Selecting an aircraft opens this card. Its buttons show the clearances available right now; clicking one sends the call. The first task is to select ELY312 yourself.', prepare: () => onSelect('ELY312') },
  ].filter((stop) => root.querySelector(stop.target) && (stop.target === '.acard' || !(root.querySelector(stop.target) as HTMLElement).hidden));
  let index = 0;
  let closed = false;
  const blocker = document.createElement('div'); blocker.className = 'tutorial-tour-blocker';
  const spotlight = document.createElement('div'); spotlight.className = 'tutorial-tour-spotlight';
  const tip = document.createElement('section'); tip.className = 'tutorial-tour'; tip.setAttribute('role', 'dialog'); tip.setAttribute('aria-modal', 'true'); tip.setAttribute('aria-label', 'Tutorial tour');
  tip.innerHTML = '<span class="tour-progress"></span><h2></h2><p></p><div class="tour-actions"><button type="button" data-tour-skip>Skip tour</button><button type="button" data-tour-back>Back</button><button type="button" data-tour-next>Next</button></div>';
  document.body.append(blocker, spotlight, tip);
  const position = () => {
    if (closed) return;
    const target = root.querySelector(stops[index]!.target) as HTMLElement | null;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const pad = 5;
    Object.assign(spotlight.style, { left: `${Math.max(0, rect.left - pad)}px`, top: `${Math.max(0, rect.top - pad)}px`, width: `${Math.min(innerWidth, rect.right + pad) - Math.max(0, rect.left - pad)}px`, height: `${Math.min(innerHeight, rect.bottom + pad) - Math.max(0, rect.top - pad)}px` });
    const width = Math.min(360, innerWidth - 24);
    const below = rect.bottom + 14;
    const tipHeight = tip.getBoundingClientRect().height;
    const top = below + tipHeight <= innerHeight - 12 ? below : Math.max(12, rect.top - tipHeight - 14);
    tip.style.width = `${width}px`;
    tip.style.left = `${Math.max(12, Math.min(rect.left, innerWidth - width - 12))}px`;
    tip.style.top = `${Math.max(12, Math.min(top, innerHeight - tipHeight - 12))}px`;
  };
  const show = (next: number) => {
    index = next;
    const stop = stops[index]!;
    stop.prepare?.();
    tip.querySelector('.tour-progress')!.textContent = `SCREEN TOUR  ·  ${index + 1} / ${stops.length}`;
    tip.querySelector('h2')!.textContent = stop.title;
    tip.querySelector('p')!.textContent = stop.body;
    (tip.querySelector('[data-tour-back]') as HTMLButtonElement).disabled = index === 0;
    (tip.querySelector('[data-tour-next]') as HTMLButtonElement).textContent = index === stops.length - 1 ? 'Start tutorial' : 'Next';
    const target = root.querySelector(stop.target) as HTMLElement;
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    requestAnimationFrame(position);
    requestAnimationFrame(() => requestAnimationFrame(position));
    (tip.querySelector('[data-tour-next]') as HTMLButtonElement).focus();
  };
  const cleanup = () => {
    if (closed) return;
    closed = true;
    blocker.remove(); spotlight.remove(); tip.remove();
    window.removeEventListener('resize', position);
    document.removeEventListener('scroll', position, true);
    document.removeEventListener('keydown', onKey, true);
    onSelect(null);
  };
  const finish = () => { cleanup(); onDone(); };
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); finish(); return; }
    if (event.key === 'Tab') {
      const buttons = [...tip.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
      const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (event.shiftKey && current <= 0) { event.preventDefault(); buttons.at(-1)?.focus(); }
      else if (!event.shiftKey && current === buttons.length - 1) { event.preventDefault(); buttons[0]?.focus(); }
      return;
    }
    if (!tip.contains(event.target as Node)) { event.preventDefault(); event.stopImmediatePropagation(); }
  };
  tip.querySelector('[data-tour-skip]')!.addEventListener('click', finish);
  tip.querySelector('[data-tour-back]')!.addEventListener('click', () => show(index - 1));
  tip.querySelector('[data-tour-next]')!.addEventListener('click', () => index === stops.length - 1 ? finish() : show(index + 1));
  window.addEventListener('resize', position);
  document.addEventListener('scroll', position, true);
  document.addEventListener('keydown', onKey, true);
  show(0);
  return cleanup;
}
